import { HttpErrorService } from '@/services/HttpError.service'
import { certificateErrorHandbook } from '@/models/Errors'
import { HttpService } from '@/services/Http.service'
import type {
  Certificate,
  CertificateOrder,
  CertificatePrint,
  CertificateTemplate,
  OrderInfoPageKit,
  Payment,
} from '~/models'

export class CertificateService {
  private endpoint = '/certificates'
  private httpErrorService: HttpErrorService<
    keyof typeof certificateErrorHandbook
  >

  private httpService: HttpService

  constructor(httpService: HttpService) {
    this.httpService = httpService
    this.httpErrorService = new HttpErrorService(
      certificateErrorHandbook,
      'certificate',
    )
  }

  async create(order: CertificateOrder) {
    const url = [this.endpoint, 'init'].join('/')

    return await this.httpService
      .post<Payment>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
          secure: true,
        },
        fetchConfig: {
          body: order,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // получение списка сертификатов для магазина сертификатов
  async getAll() {
    const url = this.endpoint

    return await this.httpService
      .get<Certificate[]>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // принять подареный сертификат
  async accept(id: number) {
    const url = [this.endpoint, 'accept'].join('/')

    return await this.httpService
      .post<Payment>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: {
            id,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // отправить сертификат в подарок
  async send(id: number, email: string) {
    const url = [this.endpoint, 'send'].join('/')

    return await this.httpService
      .post<Payment>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: {
            id,
            email,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // отменить отправку сертификата
  async cancel(id: number) {
    const url = [this.endpoint, 'cancel'].join('/')

    return await this.httpService
      .post<Payment>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: {
            id,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // получение шаблонов сертификатов для распечатки
  async getTemplates(certificateId: number) {
    const url = [this.endpoint, 'templates'].join('/')

    return await this.httpService
      .post<CertificateTemplate[]>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: {
            certificateId,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // получение ссылки на пдф с сертификатом
  async print(data: CertificatePrint) {
    const url = [this.endpoint, 'print'].join('/')

    return await this.httpService
      .post<Blob | MediaSource>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
          secure: true,
        },
        fetchConfig: {
          body: data,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  // добавление сертификата по номеру (названию) в ЛК
  async addByName(name: string) {
    const url = [this.endpoint, 'add'].join('/')

    return await this.httpService
      .post(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: {
            name,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getCertificatesThankYouPage(id: string, code: string) {
    const url = [this.endpoint, 'ty', id].join('/')

    return await this.httpService
      .get<OrderInfoPageKit>(url, {
        requestConfig: {
          forceRequest: true,
          needGlobal404: true,
          secure: true,
        },
        fetchConfig: {
          params: {
            code,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }
}
